import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Row, Select, Spin, Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import { RightOutlined, LeftOutlined, SearchOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../..";
import { checkIsLoading } from "../../../lib/utils";
import type { EmptyFunction } from "../../../lib/types";
import "./ReportsCatalog.less";
import { ReportCatalogItem } from "../../../lib/interfaces/Report";
import { DebounceSelect } from "./DebounceSelect/DebounceSelect";
import { ReportsSearchValues, ReportsSelectedFilterValues, ReportsTreeData } from "./ReportsTypes";

interface ComponentProps {
  isCatalogOpen: boolean;
  toggleCatalogOpen: EmptyFunction;
  catalogExpandedKeys: string[];
  setCatalogExpandedKeys: Dispatch<SetStateAction<string[]>>;
  onChangeSearchValue: (field: ReportsSelectedFilterValues) => void;
  searchListValues: ReportsSelectedFilterValues;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ReportsCatalog = (props: ComponentProps) => {
  const {
    isCatalogOpen,
    toggleCatalogOpen,
    catalogExpandedKeys,
    setCatalogExpandedKeys,
    onChangeSearchValue,
    searchListValues,
  } = props;

  const { reportsCatalog, reportsCatalogLoading } = useAppSelector((state) => state.reports);

  const [value, setValue] = useState<ReportsSearchValues>();

  const onSelect = (_, { node }) => {
    if (node.type === "report_group") return;

    const [reportGroup, report] = String(node.key).split("_");

    const [reportGroupType, reportGroupId] = String(reportGroup).split(":");
    const [reportType, reportId] = String(report).split(":");

    onChangeSearchValue({
      reportGroupId: undefined,
      reportId: undefined,
      [reportGroupType]: Number(reportGroupId),
      [reportType]: Number(reportId),
    });
  };

  const selectedKeys = useMemo(() => {
    const { reportGroupId, reportId } = searchListValues;
    if (reportGroupId !== undefined && reportId !== undefined) {
      return [`reportGroupId:${reportGroupId}_reportId:${reportId}`];
    }

    return [];
  }, [searchListValues]);

  const treeData = useMemo((): ReportsTreeData[] => {
    const mapCatalogToTree = (
      items: ReportCatalogItem[],
      parentGroupId?: number
    ): ReportsTreeData[] => {
      if (!items?.length) return [];

      return [...items]
        .sort((a, b) => a?.name?.localeCompare(b?.name, "ru"))
        .map(({ id, name, children, type }) => {
          const childrenTreeData =
            children?.length && type === "report_group" ? mapCatalogToTree(children, id) : [];

          return {
            title: name || "Нет названия",
            key: `reportGroupId:${parentGroupId}_reportId:${id}`,
            type,
            children: childrenTreeData,
          };
        });
    };

    return mapCatalogToTree(reportsCatalog);
  }, [reportsCatalog]);

  const divTitleRef = useRef<HTMLDivElement>(null);

  const resizeHandler = () => {
    const { offsetHeight } = divTitleRef.current || {};
    const divTreeElements: NodeListOf<HTMLDivElement> | null = document?.querySelectorAll(
      ".ant-tree"
    );
    if (divTreeElements?.length > 0) {
      divTreeElements.forEach((el) => {
        el.style.height = `calc(100% - ${offsetHeight || 0}px)`;
      });
    }
  };

  useEffect(() => {
    window.addEventListener("resize", resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [divTitleRef.current]);

  const handleSearch = (value: string): ReportsSearchValues[] => {
    if (value.length < 3) {
      return [];
    }

    const flattenTree = (
      nodes: ReportsTreeData[],
      parentKeys: string[] = []
    ): ReportsSearchValues[] => {
      return nodes.reduce((acc: ReportsSearchValues[], node) => {
        const currentKeys = [...parentKeys, node.key];
        acc.push({
          key: node.key,
          label: node.title,
          value: node.key,
          type: node.type,
          parentKeys: currentKeys,
        });
        if (node.children) {
          acc = acc.concat(flattenTree(node.children, currentKeys));
        }
        return acc;
      }, []);
    };

    const flatTreeData = flattenTree(treeData);
    const filtered = flatTreeData.filter(
      (item) =>
        String(item.label).toLowerCase().includes(value.toLowerCase()) &&
        item.type !== "report_group"
    );

    return filtered;
  };

  const handleSelectSearch = (selectValue: ReportsSearchValues) => {
    const flattenTree = (
      nodes: ReportsTreeData[],
      parentKeys: string[] = []
    ): ReportsSearchValues[] => {
      return nodes.reduce((acc: ReportsSearchValues[], node) => {
        const currentKeys = [...parentKeys, node.key];
        acc.push({
          key: node.key,
          label: node.title,
          value: node.key,
          type: node.type,
          parentKeys: currentKeys,
        });
        if (node.children) {
          acc = acc.concat(flattenTree(node.children, currentKeys));
        }
        return acc;
      }, []);
    };

    const flatTreeData = flattenTree(treeData);
    const selectedNode = flatTreeData.find((item) => item.key === selectValue.value);

    if (selectedNode) {
      setCatalogExpandedKeys(selectedNode.parentKeys);
      onSelect(undefined, { node: selectedNode });
      setTimeout(() => {
        const element = document.querySelector(`[title="${selectedNode.label}"]`);
        element && element.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  };

  return (
    <Spin spinning={checkIsLoading(reportsCatalogLoading)} style={{ height: "100%" }}>
      <div className="reports-catalog">
        {isCatalogOpen && (
          <div className="reports-catalog-opened">
            <Row className="reports-catalog-opened-search">
              <div ref={divTitleRef} className="reports-catalog-opened__title">
                <h3>Каталог отчетов</h3>
                <LeftOutlined style={{ marginLeft: "auto" }} onClick={toggleCatalogOpen} />
              </div>

              <DebounceSelect
                className="reports-catalog-opened-search__select"
                value={value}
                showSearch
                allowClear
                placeholder="Поиск"
                suffixIcon={<SearchOutlined />}
                handleSearch={handleSearch}
                onChange={(newValue) => {
                  setValue(newValue as ReportsSearchValues);
                }}
                onSelect={handleSelectSearch}
              />
            </Row>
            <Tree
              selectedKeys={selectedKeys}
              showLine={{ showLeafIcon: false }}
              treeData={treeData}
              showIcon={false}
              onSelect={onSelect}
              onExpand={(keys) => setCatalogExpandedKeys(keys.map(String))}
              expandedKeys={catalogExpandedKeys}
            />
          </div>
        )}

        {!isCatalogOpen && (
          <div className="reports-catalog-closed">
            <div>
              <RightOutlined onClick={toggleCatalogOpen} />
            </div>
            <div className="reports-catalog-closed__title">
              <h3>Каталог отчетов</h3>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};
